import { PlusCircleIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import '~/components/pageHeaders.css';
import { FilterProps, TableFilter } from '../Table/TableFilter';

interface TableCardProps {
  /** Use the phone page-heading contract without changing desktop or nested-card defaults. */
  mobilePageHeader?: boolean;
  children: React.ReactNode;
  title: string;
  addLabel: string;
  onAddClick: () => void;
  layoutTag: string;
  addBtnTag: string;
  filter?: FilterProps;
  customButtons?: React.ReactNode | React.ReactNode[];
  /** Replaces the single add button, for a page whose "add" asks which kind first. */
  addControl?: React.ReactNode;
  /** Sits immediately left of the add button / control, e.g. a tutorial-video button. */
  addAccessory?: React.ReactNode;
  customClasses?: string;
  width?: string;
  height?: string;
  disabledBtnTooltip?: boolean;
  disabledTitle?: string;
}

export const TableCard = (props: TableCardProps) => {
  const {
    children,
    title,
    addBtnTag,
    layoutTag,
    onAddClick,
    addLabel,
    filter,
    width,
    height,
    disabledBtnTooltip: addBtnDisabled,
    disabledTitle,
    customClasses,
    addControl,
    addAccessory,
  } = props;
  return (
    <div
      className={classNames(
        'p-5 sm:p-8 bg-white rounded-xl shadow-sm border border-grey-900 w-full mx-auto',
        props.mobilePageHeader && 'mobile-page-card',
        width,
        height,
        customClasses
      )}
    >
      <div
        className={classNames(
          'flex flex-col sm:flex-row sm:items-center gap-3 pb-5 sm:pb-6 mb-5 sm:mb-6 border-b border-grey-900',
          props.mobilePageHeader && 'mobile-page-header'
        )}
        data-cy={layoutTag}
      >
        <h2 className="sub-heading-sb text-grey-100">{title}</h2>
        {filter && (
          <div className="sm:ml-4">
            <TableFilter {...filter} />
          </div>
        )}
        {props.customButtons && (
          <div
            className={classNames(
              'sm:ml-4 flex flex-row gap-4',
              props.mobilePageHeader && 'mobile-page-actions'
            )}
          >
            {Array.isArray(props.customButtons)
              ? props.customButtons.map((button, idx) => <div key={idx}>{button}</div>)
              : props.customButtons}
          </div>
        )}
        <div
          className={classNames('sm:ml-auto', Boolean(addAccessory) && 'flex items-center gap-3')}
        >
          {addAccessory}
          {addControl ?? (
            <button
              type="button"
              data-cy={addBtnTag}
              className={classNames(
                'flex items-center gap-2 rounded-lg px-3.5 py-2 body-text-sb',
                'text-primary-50 border border-primary-t-700 hover:bg-primary-t-900 hover:border-primary-t-600 transition-colors enabled:cursor-pointer',
                { 'opacity-50 cursor-not-allowed': addBtnDisabled }
              )}
              onClick={onAddClick}
              disabled={addBtnDisabled}
              title={addBtnDisabled ? disabledTitle : ''}
            >
              <PlusCircleIcon className="size-5" aria-hidden="true" />
              {addLabel}
            </button>
          )}
        </div>
      </div>
      {children}
    </div>
  );
};
